import { useI18n } from '../i18n';
import { Header } from './Header';
import { UpdateBanner } from './UpdateBanner';

/**
 * Coquille applicative : bandeau de mise à jour, lien d'évitement, en-tête et
 * zone de contenu principale. Colonne EXACTEMENT à la hauteur du viewport
 * (`h-dvh` + `overflow-hidden`) : la coquille NE défile PAS. L'en-tête ne rétrécit
 * pas (`shrink-0`) ; la zone principale prend le reste (`flex-1 min-h-0`) et PORTE
 * son propre défilement vertical (`overflow-y-auto`) — sans quoi une page plus haute
 * que le viewport serait rognée par l'`overflow-hidden` de la coquille (aucune page
 * n'ayant de conteneur de défilement propre). Les écrans s'insèrent via `children` ;
 * la navigation via `nav`.
 */
interface AppShellProps {
  children: React.ReactNode;
  /** Slot de navigation transmis à l'en-tête. */
  nav?: React.ReactNode;
  /** Actions de fin d'en-tête (ex. menu utilisateur), posées par l'orchestrateur. */
  headerActions?: React.ReactNode;
}

export function AppShell({ children, nav, headerActions }: AppShellProps): React.JSX.Element {
  const { t } = useI18n();

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-surface text-fg">
      <UpdateBanner />
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-30 focus:m-2 focus:rounded-md focus:bg-brand-strong focus:px-3 focus:py-2 focus:text-brand-fg"
      >
        {t('a11y.skipToContent')}
      </a>
      <Header nav={nav} actions={headerActions} />
      {/*
       * `relative` (sans offset = no-op visuel ; n'affecte PAS les `position:fixed`) :
       * établit le BLOC CONTENEUR de tout descendant `position:absolute` des pages
       * rendues via `children`. Sans lui, un `sr-only` (Tailwind pose `position:absolute`)
       * enfoui SANS ancêtre positionné s'ancre au « Initial Containing Block » du document
       * et GONFLE la barre de défilement du document → une 2e barre déplace toute la page
       * (fil d'Ariane compris). Ancrer ici couvre TOUTES les pages en une fois (garde
       * par-page en défense en profondeur).
       */}
      <main
        id="main"
        className="relative mx-auto flex w-full min-h-0 max-w-5xl flex-1 flex-col overflow-y-auto px-4 py-4"
      >
        {children}
      </main>
    </div>
  );
}
